import { NavLink, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  X,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from 'lucide-react'
import { NAV_GROUPS } from '../../Models/navigationData'
import { logoutUser } from '../Login/services/authSlice'

export default function Sidebar({ mobileOpen, onClose, collapsed, onToggle }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const handleLogout = async () => {
    onClose()
    await dispatch(logoutUser())
    navigate('/')
  }

  return (
    <>
      {mobileOpen && <div className="sb-backdrop" onClick={onClose} />}

      <aside className={`sidebar${mobileOpen ? ' sidebar--open' : ''}${collapsed ? ' sidebar--collapsed' : ''}`}>

        {/* Logo */}
        <div className="sb-logo">
          <img
            src="https://supadaa.com/images/supadaa_logo_black.png"
            alt="Supadaa"
            className="sb-logo-img sb-logo-img--light"
          />
          <img
            src="https://supadaa.com/images/supadaa_logo_white.png"
            alt="Supadaa"
            className="sb-logo-img sb-logo-img--dark"
          />
          <button className="sb-close" onClick={onClose} aria-label="Close">
            <X size={15} />
          </button>
        </div>

        {/* Nav */}
        <nav className="sb-nav" aria-label="Main navigation">
          {NAV_GROUPS.map(({ label, items }) => (
            <div key={label} className="sb-group">
              {collapsed
                ? <div className="sb-group-divider" />
                : <div className="sb-group-label">{label}</div>
              }
              {items.map(({ path, label: lbl, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  onClick={onClose}
                  title={collapsed ? lbl : undefined}
                  className={({ isActive }) =>
                    `sb-item${isActive ? ' sb-item--active' : ''}${collapsed ? ' sb-item--icon' : ''}`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className="sb-icon">
                        <Icon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
                      </span>
                      {!collapsed && <span className="sb-label">{lbl}</span>}
                      {isActive && !collapsed && <span className="sb-pip" />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Logout button */}
        <button
          className="sb-logout"
          onClick={handleLogout}
          title="Logout"
          aria-label="Logout"
        >
          <LogOut size={14} />
          {!collapsed && <span>Logout</span>}
        </button>

        {/* Collapse toggle */}
        <button
          className="sb-toggle"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? <ChevronRight size={14} />
            : <><ChevronLeft size={14} /><span>Collapse</span></>
          }
        </button>

      </aside>

      <style>{`
        .sidebar {
          width: var(--sidebar-width);
          height: 100vh;
          background: var(--bg-surface);
          border-right: 1px solid var(--border);
          display: flex; flex-direction: column;
          position: fixed; left: 0; top: 0;
          z-index: 100;
          transition: width var(--ease-slow), transform var(--ease-slow);
          overflow: hidden; flex-shrink: 0;
        }
        .sidebar--collapsed { width: var(--sidebar-collapsed-width); }
        .sb-backdrop {
          position: fixed; inset: 0;
          background: rgba(0,0,0,.65);
          z-index: 99; backdrop-filter: blur(3px);
        }
        .sb-logo {
          height: var(--header-height);
          padding: 0 var(--space-md);
          border-bottom: 1px solid var(--border);
          display: flex; align-items: center;
          justify-content: center;
          position: relative;
          flex-shrink: 0; overflow: hidden;
        }
        .sb-logo-img {
          height: 42px;
          width: auto;
          max-width: 80%;
          object-fit: contain;
        }
        .sb-close {
          display: none; background: none; border: none;
          cursor: pointer; color: var(--text3); padding: 4px;
          align-items: center; justify-content: center;
          border-radius: var(--radius-sm);
          position: absolute; right: var(--space-md);
          flex-shrink: 0; transition: color var(--ease-fast);
        }
        .sb-close:hover { color: var(--text1); background: var(--bg-hover); }
        .sb-nav {
          flex: 1; overflow-y: auto; overflow-x: hidden; padding: var(--space-sm) 0;
        }
        .sb-nav::-webkit-scrollbar { width: 2px; }
        .sb-nav::-webkit-scrollbar-thumb { background: var(--border-active); }
        .sb-group { margin-bottom: 4px; }
        .sb-group-label {
          font-size: 10px; font-weight: 600; color: var(--text4);
          letter-spacing: 0.1em; text-transform: uppercase;
          padding: var(--space-sm) var(--space-md) 4px;
        }
        .sb-group-divider {
          height: 1px; background: var(--border-subtle);
          margin: var(--space-sm) var(--space-sm);
        }
        .sb-item {
          display: flex; align-items: center; gap: 0.6rem;
          padding: 0.5rem var(--space-md);
          margin: 2px var(--space-xs);
          text-decoration: none; font-size: var(--fs-sm); font-weight: 500;
          color: var(--text3); border-radius: var(--radius-sm);
          transition: all var(--ease-fast); position: relative; white-space: nowrap;
        }
        .sb-item:hover { background: var(--bg-hover); color: var(--text2); }
        .sb-item--active { background: var(--bg-active); color: var(--accent-hover); font-weight: 600; }
        .sb-item--icon {
          padding: 0.5rem; margin: 2px auto;
          width: calc(var(--sidebar-collapsed-width) - 15px); justify-content: center;
        }
        .sb-icon {
          display: flex; align-items: center; justify-content: center;
          width: 18px; height: 18px; flex-shrink: 0; color: inherit;
        }
        .sb-label { flex: 1; overflow: hidden; text-overflow: ellipsis; }
        .sb-pip {
          width: 5px; height: 5px; border-radius: 50%;
          background: var(--accent-hover); flex-shrink: 0;
          box-shadow: 0 0 8px var(--accent-glow);
        }
        .sb-toggle {
          display: flex; align-items: center; gap: var(--space-xs);
          padding: var(--space-sm) var(--space-md);
          margin: 10px 15px 15px; flex-shrink: 0;
          border: 1px solid var(--border); background: var(--bg-raised);
          border-radius: var(--radius-sm); color: var(--text3);
          font-size: var(--fs-xs); font-weight: 500; cursor: pointer;
          white-space: nowrap;
          overflow: hidden; transition: all var(--ease-fast);
          font-family: var(--font-body);
        }
        .sidebar--collapsed .sb-toggle {
          justify-content: center; 
          margin: var(--space-xs) auto;
          width: calc(var(--sidebar-collapsed-width) - 15px);
        }
        .sb-toggle:hover { border-color: var(--border-active); color: var(--text1); background: var(--bg-hover); }

        .sb-logout {
          display: flex; align-items: center; gap: var(--space-xs);
          padding: var(--space-sm) var(--space-md);
          margin: 0 15px;
          border: 1px solid rgba(239, 68, 68, 0.25);
          background: rgba(239, 68, 68, 0.06);
          border-radius: var(--radius-sm);
          color: var(--color-red);
          font-size: var(--fs-sm); font-weight: 500; cursor: pointer;
          white-space: nowrap;
          overflow: hidden; transition: all var(--ease-fast);
          font-family: var(--font-body); flex-shrink: 0;
        }
        .sb-logout:hover {
          background: rgba(239, 68, 68, 0.14);
          border-color: rgba(239, 68, 68, 0.45);
          color: var(--color-red);
        }
        .sidebar--collapsed .sb-logout {
          justify-content: center;
          margin: 0 auto 5px;
          width: calc(var(--sidebar-collapsed-width) - 15px);
          padding: 0.5rem;
        }

        .sb-footer {
          margin: 0 var(--space-sm) var(--space-sm);
          padding: var(--space-sm);
          border-radius: var(--radius-md);
          border: 1px solid var(--border);
          background: linear-gradient(135deg, rgba(79,70,229,.12), rgba(6,182,212,.08));
          display: flex; align-items: center; gap: var(--space-sm);
        }
        .sb-footer-icon {
          width: 24px; height: 24px; border-radius: 7px;
          display: grid; place-items: center; color: #fff;
          background: linear-gradient(135deg, var(--accent), var(--accent-2));
          box-shadow: 0 8px 18px rgba(79,70,229,.3); flex-shrink: 0;
        }
        .sb-footer-copy { display: flex; flex-direction: column; min-width: 0; }
        .sb-footer-copy span {
          font-size: var(--fs-xs); font-weight: 600; color: var(--text1); letter-spacing: .02em;
        }
        .sb-footer-copy small {
          font-size: 10px; color: var(--text3); white-space: nowrap;
          overflow: hidden; text-overflow: ellipsis;
        }

        @media (min-width: 769px) {
          .sidebar { transform: translateX(0) !important; }
          .sb-close { display: none !important; }
        }
        @media (max-width: 768px) {
          .sidebar { transform: translateX(-100%); width: 240px !important; }
          .sidebar--open { transform: translateX(0); }
          .sidebar--collapsed { width: 240px !important; }
          .sb-close { display: flex !important; }
          .sb-toggle { display: none; }
          .sb-footer { display: none; }
          .sb-logout { margin-bottom: 10px; }
        }
      `}</style>
    </>
  )
}